import { GeneratePDF } from '../../ui/pdf/GeneratePDF';
import {
  buildMealFoodDetailsMap,
  scaledNutritionToDisplay,
} from '../nutrients/dietStructureNutrition';
import { formatIntakeTimeForDisplay } from '../diet-plan/intakeTime';

const EMPTY_PDF_GOALS = {
  ENERC_KCAL: 0,
  PROCNT: 0,
  CHOCDF: 0,
  FAT: 0,
  FIBTG: 0,
  SUGAR: 0,
};

export function transformUserDataForPDF(userResponse) {
  const data = userResponse?.data || userResponse || {};

  const calculateAge = (dob) => {
    if (!dob) return 0;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  return {
    firstName: data.profile?.first_name || '',
    lastName: data.profile?.last_name || '',
    age: calculateAge(data.profile?.date_of_birth),
    gender: data.profile?.gender?.description || 'Not specified',
    height: data.heights?.[0]?.height || 0,
    height_units: data.heights?.[0]?.height_unit?.description || 'cm',
    weight: data.weights?.[0]?.weight || 0,
    weight_units: data.weights?.[0]?.weight_unit?.description || 'kg',
    email: data.emails?.[0]?.email_address || '',
    phone: data.phones?.[0]?.phone_number || '',
    dietary_preferences:
      data.dietary_preferences
        ?.map((d) => d.dietary_preference?.description)
        .join(', ') || '',
    fitness_goals:
      data.fitness_goals?.map((f) => f.fitness_goal?.description).join(', ') || '',
    medical_conditions:
      data.medical_histories
        ?.map((m) => m.medical_history?.description)
        .join(', ') || '',
  };
}

export function mapPresetNutritionToPdfGoals(preset) {
  if (!preset || preset.calories == null) return { ...EMPTY_PDF_GOALS };
  return {
    ENERC_KCAL: parseFloat(preset.calories ?? 0),
    PROCNT: parseFloat(preset.proteins ?? 0),
    CHOCDF: parseFloat(preset.carbs ?? 0),
    FAT: parseFloat(preset.fats ?? 0),
    FIBTG: parseFloat(preset.fiber ?? 0),
    SUGAR: parseFloat(preset.sugars ?? 0),
  };
}

function buildEmptyMealTotals(planData) {
  const totals = {};
  planData.meals?.forEach((meal, mealIdx) => {
    const mealType = meal.meal_type_description || meal.meal_type || `Meal ${mealIdx + 1}`;
    totals[mealType] = {};
    meal.meal_options?.forEach((option, optIdx) => {
      const optionTitle = option.title || `Option ${optIdx + 1}`;
      totals[mealType][optionTitle] = {
        foods: [],
        totalCalories: 0,
        totalProtein: 0,
        totalCarbs: 0,
        totalFats: 0,
        totalFiber: 0,
        totalSodium: 0,
        totalSugar: 0,
      };
    });
  });
  return totals;
}

export function calculateGrandTotals(mealTypeTotals) {
  const grandTotals = {
    calories: 0,
    protein: 0,
    carbs: 0,
    fats: 0,
    fiber: 0,
    sodium: 0,
    sugar: 0,
    foodCount: 0,
  };

  Object.values(mealTypeTotals || {}).forEach((options) => {
    Object.values(options).forEach((optionData) => {
      grandTotals.calories += optionData.totalCalories || 0;
      grandTotals.protein += optionData.totalProtein || 0;
      grandTotals.carbs += optionData.totalCarbs || 0;
      grandTotals.fats += optionData.totalFats || 0;
      grandTotals.fiber += optionData.totalFiber || 0;
      grandTotals.sodium += optionData.totalSodium || 0;
      grandTotals.sugar += optionData.totalSugar || 0;
      grandTotals.foodCount += optionData.foods?.length || 0;
    });
  });

  Object.keys(grandTotals).forEach((key) => {
    if (key !== 'foodCount') {
      grandTotals[key] = parseFloat(grandTotals[key].toFixed(2));
    }
  });

  return grandTotals;
}

/**
 * Build meal_type_totals and enriched meals for GeneratePDF from plan API data.
 */
export function enrichPlanDataForPdf(planData, existingDetailsMap = {}) {
  const detailsMap = buildMealFoodDetailsMap(planData?.meals || [], existingDetailsMap);

  if (Object.keys(detailsMap).length === 0) {
    return {
      enrichedPlan: {
        ...planData,
        meal_type_totals: buildEmptyMealTotals(planData),
        planTitle: planData?.title || 'Diet Plan',
        planDescription: planData?.description || '',
        duration: planData?.durations?.[0] || {},
      },
      grandTotals: calculateGrandTotals(buildEmptyMealTotals(planData)),
    };
  }

  const enrichedPlan = JSON.parse(JSON.stringify(planData));
  enrichedPlan.meal_type_totals = {};

  enrichedPlan.meals = enrichedPlan.meals?.map((meal, mealIdx) => {
    const mealType = meal.meal_type_description || meal.meal_type || `Meal ${mealIdx + 1}`;

    if (!enrichedPlan.meal_type_totals[mealType]) {
      enrichedPlan.meal_type_totals[mealType] = {};
    }

    return {
      ...meal,
      meal_options: meal.meal_options?.map((option, optIdx) => {
        const optionTitle = option.title || `Option ${optIdx + 1}`;

        if (!enrichedPlan.meal_type_totals[mealType][optionTitle]) {
          enrichedPlan.meal_type_totals[mealType][optionTitle] = {
            foods: [],
            totalCalories: 0,
            totalProtein: 0,
            totalCarbs: 0,
            totalFats: 0,
            totalFiber: 0,
            totalSodium: 0,
            totalSugar: 0,
          };
        }

        return {
          ...option,
          foods: option.foods?.map((food) => {
            const foodId = food?.food_id || food?.target_food_id;
            if (!foodId) return food;
            const detail = detailsMap[String(foodId)];

            if (detail) {
              const nutrition = scaledNutritionToDisplay(food.scaled_nutrition || {});
              const formattedTime = formatIntakeTimeForDisplay(food.intake_time);

              const enrichedFood = {
                ...food,
                food_name:
                  detail.food_name ||
                  detail.foodName ||
                  food.food_name ||
                  'Unknown Food',
                common_names: detail.common_names || '',
                ingredients_text: detail.ingredients_text || '',
                calories_calculated_for: detail.calories_calculated_for || 100,
                intake_time_formatted: formattedTime,
                calories: nutrition.calories,
                protein: nutrition.proteins ?? nutrition.protein,
                carbs: nutrition.carbs,
                fats: nutrition.fats,
                fiber: nutrition.fiber,
                sodium: 0,
                sugar: nutrition.sugars ?? nutrition.sugar,
                saturated_fats: 0,
                nutrients: detail.nutrients || {},
                measures: detail.measures || [],
                quantity_display: `${food.quantity} ${
                  food.quantity_type_description || food.quantity_type || 'serving'
                }`,
                food_details: {
                  main_details: {
                    food_name: detail.food_name,
                    common_names: detail.common_names || '',
                    calories_calculated_for: detail.calories_calculated_for || 100,
                  },
                  nutrition: detail.nutrients || {},
                  measures: detail.measures || [],
                },
              };

              const foodEntry = {
                name: enrichedFood.food_name,
                quantity: food.quantity || 1,
                unit:
                  food.quantity_type_description || food.quantity_type || 'serving',
                calories: parseFloat(nutrition.calories),
                protein: parseFloat(nutrition.proteins ?? nutrition.protein),
                carbs: parseFloat(nutrition.carbs),
                fats: parseFloat(nutrition.fats),
                fiber: parseFloat(nutrition.fiber),
                sugar: parseFloat(nutrition.sugars ?? nutrition.sugar),
                intake_time: formattedTime,
              };

              const totals = enrichedPlan.meal_type_totals[mealType][optionTitle];
              totals.foods.push(foodEntry);
              totals.totalCalories += foodEntry.calories;
              totals.totalProtein += foodEntry.protein;
              totals.totalCarbs += foodEntry.carbs;
              totals.totalFats += foodEntry.fats;
              totals.totalFiber += foodEntry.fiber;
              totals.totalSugar += foodEntry.sugar;

              return enrichedFood;
            }

            const minimalFood = {
              ...food,
              food_name: food.food_name || food.recipe_name || 'Unknown Food',
              calories: 0,
              protein: 0,
              carbs: 0,
              fats: 0,
              fiber: 0,
              sodium: 0,
              sugar: 0,
              quantity_display: `${food.quantity || 1} ${
                food.quantity_type_description || 'serving'
              }`,
            };
            const scaled = scaledNutritionToDisplay(food.scaled_nutrition || {});
            if (scaled.calories || scaled.proteins || scaled.carbs) {
              const formattedTime = formatIntakeTimeForDisplay(food.intake_time);
              const foodEntry = {
                name: minimalFood.food_name,
                quantity: food.quantity || 1,
                unit: food.quantity_type_description || food.quantity_type || 'serving',
                calories: parseFloat(scaled.calories) || 0,
                protein: parseFloat(scaled.proteins) || 0,
                carbs: parseFloat(scaled.carbs) || 0,
                fats: parseFloat(scaled.fats) || 0,
                fiber: parseFloat(scaled.fiber) || 0,
                sugar: parseFloat(scaled.sugars) || 0,
                intake_time: formattedTime,
              };
              const totals = enrichedPlan.meal_type_totals[mealType][optionTitle];
              totals.foods.push(foodEntry);
              totals.totalCalories += foodEntry.calories;
              totals.totalProtein += foodEntry.protein;
              totals.totalCarbs += foodEntry.carbs;
              totals.totalFats += foodEntry.fats;
              totals.totalFiber += foodEntry.fiber;
              totals.totalSugar += foodEntry.sugar;
            } else {
              enrichedPlan.meal_type_totals[mealType][optionTitle].foods.push({
                name: minimalFood.food_name,
                quantity: food.quantity || 1,
                unit: food.quantity_type_description || 'serving',
                calories: 0,
                protein: 0,
                carbs: 0,
                fats: 0,
                fiber: 0,
                sugar: 0,
                intake_time: formatIntakeTimeForDisplay(food.intake_time),
              });
            }

            return minimalFood;
          }),
        };
      }),
    };
  });

  const grandTotals = calculateGrandTotals(enrichedPlan.meal_type_totals);
  enrichedPlan.grandTotals = grandTotals;
  enrichedPlan.planTitle = planData?.title || 'Diet Plan';
  enrichedPlan.planDescription = planData?.description || '';
  enrichedPlan.duration = planData?.durations?.[0] || {};

  return { enrichedPlan, grandTotals };
}

export async function resolveCoachForPdf({
  profileEventTypeId,
  assignedById,
  getUserProfileDetails,
  defaultName = 'Coach',
  defaultEmail = '',
}) {
  let coachName = defaultName;
  let coachEmail = defaultEmail;

  if (!assignedById || !profileEventTypeId) {
    return { coachName, coachEmail };
  }

  try {
    const coachResponse = await getUserProfileDetails(profileEventTypeId, assignedById);
    if (coachResponse?.success && coachResponse?.data) {
      const coachData = coachResponse.data.data || coachResponse.data;
      const firstName = coachData.profile?.first_name || '';
      const lastName = coachData.profile?.last_name || '';
      const resolvedName = `${firstName} ${lastName}`.trim();
      coachName = resolvedName || defaultName;
      coachEmail =
        coachData.emails?.[0]?.email_address || coachData.email || defaultEmail;
    }
  } catch (error) {
    console.error('Error fetching coach details:', error);
  }

  return { coachName, coachEmail };
}

/**
 * Fetch user, plan, nutrition goals, coach, and enrich plan data for PDF generation.
 */
export async function prepareDietPlanPdfInputs({
  profileEventTypeId,
  dietEventTypeId,
  profileId,
  planData: planDataInput,
  planId,
  fetchTargetPlanDetails,
  getUserProfileDetails,
  fetchPresetNutrition,
  existingDetailsMap = {},
  defaultCoachName = 'Coach',
  defaultCoachEmail = '',
  onProgress,
}) {
  onProgress?.('Loading user details...');

  const userResponse = await getUserProfileDetails(profileEventTypeId, profileId);
  if (!userResponse?.success || !userResponse?.data) {
    return { success: false, message: 'Could not load user details for PDF' };
  }

  let planData = planDataInput;
  if (!planData) {
    if (!planId) {
      return { success: false, message: 'Plan data or plan id is required' };
    }
    onProgress?.('Loading plan details...');
    const planResult = await fetchTargetPlanDetails(dietEventTypeId, planId, profileId);
    if (!planResult?.success || !planResult?.data?.data) {
      return { success: false, message: 'Could not load plan details for PDF' };
    }
    planData = planResult.data.data;
  }

  onProgress?.('Fetching nutrition targets...');
  let nutritionGoals = { ...EMPTY_PDF_GOALS };
  try {
    const preset = await fetchPresetNutrition(profileEventTypeId, profileId);
    nutritionGoals = mapPresetNutritionToPdfGoals(preset);
  } catch (error) {
    console.error('Error fetching nutrition goals:', error);
  }

  const { coachName, coachEmail } = await resolveCoachForPdf({
    profileEventTypeId,
    assignedById: planData.assigned_by_id,
    getUserProfileDetails,
    defaultName: defaultCoachName,
    defaultEmail: defaultCoachEmail,
  });

  onProgress?.('Loading food details for PDF...');
  const { enrichedPlan, grandTotals } = enrichPlanDataForPdf(planData, existingDetailsMap);

  return {
    success: true,
    userData: transformUserDataForPDF(userResponse.data),
    planData: enrichedPlan,
    nutritionGoals,
    coachName,
    coachEmail,
    grandTotals,
    planTitle: planData.title,
  };
}

/**
 * End-to-end diet plan PDF: prepare inputs then render via GeneratePDF.
 */
export async function generateDietPlanPdf({
  profileEventTypeId,
  dietEventTypeId,
  profileId,
  planData,
  planId,
  fetchTargetPlanDetails,
  getUserProfileDetails,
  fetchPresetNutrition,
  existingDetailsMap = {},
  defaultCoachName = 'Coach',
  defaultCoachEmail = '',
  empId,
  get,
  onProgress,
  onEnriched,
  returnBlob = false,
  planTitle,
}) {
  const prepared = await prepareDietPlanPdfInputs({
    profileEventTypeId,
    dietEventTypeId,
    profileId,
    planData,
    planId,
    fetchTargetPlanDetails,
    getUserProfileDetails,
    fetchPresetNutrition,
    existingDetailsMap,
    defaultCoachName,
    defaultCoachEmail,
    onProgress,
  });

  if (!prepared.success) {
    return prepared;
  }

  onEnriched?.({ grandTotals: prepared.grandTotals, planData: prepared.planData });

  onProgress?.('Generating PDF...');

  const pdfResult = await GeneratePDF(
    prepared.userData,
    prepared.planData,
    get,
    prepared.coachName,
    prepared.coachEmail,
    empId,
    prepared.nutritionGoals,
    {
      returnBlob,
      planTitle: planTitle ?? prepared.planTitle,
    }
  );

  return {
    success: true,
    ...pdfResult,
    filename: pdfResult?.filename,
    blob: pdfResult?.blob,
  };
}
